"use client";

import { useTranslations } from "next-intl";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, Search, Share, Star, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ProfileActionsMenu } from "@/components/profile-page/profile-actions-menu";
import { ShareSheet } from "@/components/share/share-sheet";
import {
  PROFILE_SEARCH_ROW_HEIGHT,
  ProfileSearchField,
} from "@/components/profile-page/profile-search-field";
import { useMobile } from "@/hooks/use-mobile";
import { usePortalHost } from "@/hooks/use-portal-host";
import { useFeedSearchHeaderPortalHostsHydrated } from "@/lib/feed-portal-hosts-hydrated";
import { queryProfileHeaderPortalRoot } from "@/lib/feed-header-portals";
import { HUB_OVERLAY_BACKDROP, PROFILE_STICKY_OVERLAY_MASK } from "@/components/hub/hub-overlay-backdrop";
import { dispatchProfileStickyCompact } from "@/lib/profile-header-events";
import { profileShareUrl } from "@/lib/profile-utils";
import { ProfileTabBar } from "@/components/profile-page/profile-tab-bar";
import {
  ProfileRatingStatSkeleton,
  ProfileStatSeparator,
} from "@/components/Profile/profile-stat-inline";
import { ProfileReviewScoreLink } from "@/components/reviews/profile-review-score-link";
import {
  getProfileHeaderTitleVisible,
  PROFILE_COVER_HEIGHT_MOBILE,
} from "@/lib/profile-scroll-effects";
import { formatProfileCount } from "@/lib/profile-utils";

type ProfileStickyCompactStatsProps = {
  profileId: string;
  salesCount: number;
  isOwner: boolean;
};

function ProfileStickyCompactStats({ profileId, salesCount, isOwner }: ProfileStickyCompactStatsProps) {
  const t = useTranslations("profile");
  const salesLabel = salesCount === 1 ? t("saleLabel") : t("salesLabel");

  return (
    <div className="flex min-w-0 items-center gap-1 text-xs font-normal leading-none text-muted-foreground">
      <span className="truncate tabular-nums">
        {formatProfileCount(salesCount)} {salesLabel}
      </span>
      <ProfileReviewScoreLink
        profileId={profileId}
        tone="profile"
        isOwnProfile={isOwner}
        showSkeletonWhileLoading
        renderSkeleton={() => (
          <>
            <ProfileStatSeparator className="text-muted-foreground/50" />
            <ProfileRatingStatSkeleton compact />
          </>
        )}
        renderTrigger={({ label, onClick, hasNumericRating }) => (
          <>
            <ProfileStatSeparator className="text-muted-foreground/50" />
            <button
              type="button"
              onClick={onClick}
              className="inline-flex min-w-0 items-center gap-0.5 text-xs font-normal leading-none underline underline-offset-2 transition-opacity hover:opacity-80"
            >
              <Star
                className={cn(
                  "h-2.5 w-2.5 shrink-0",
                  hasNumericRating && "fill-amber-500 text-amber-500",
                )}
              />
              <span className="truncate tabular-nums font-normal">{label}</span>
            </button>
          </>
        )}
      />
    </div>
  );
}

type ProfileStickyHeaderProps = {
  compactTitle: string;
  isOwner: boolean;
  profileId: string;
  salesCount: number;
  username: string;
  avatarUrl?: string | null;
  bio?: string;
  currentUserId?: string | null;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  searchPlaceholder: string;
  stickySearchPlaceholder: string;
  searchCollapseStart: number;
  heroRef: React.RefObject<HTMLElement | null>;
  searchRowRef: React.RefObject<HTMLElement | null>;
  mobileScrollY?: number;
  mobileCoverHeight?: number;
  shopListingsCount?: number;
  mobileHeaderTabsOpacity?: number;
  hidden?: boolean;
};

export function ProfileStickyHeader({
  compactTitle,
  isOwner,
  profileId,
  salesCount,
  username,
  avatarUrl,
  bio,
  currentUserId,
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder,
  stickySearchPlaceholder,
  searchCollapseStart,
  heroRef,
  searchRowRef,
  mobileScrollY = 0,
  mobileCoverHeight = PROFILE_COVER_HEIGHT_MOBILE,
  shopListingsCount = 0,
  mobileHeaderTabsOpacity = 0,
  hidden = false,
}: ProfileStickyHeaderProps) {
  const t = useTranslations("profile");
  const tInfo = useTranslations("profile.info");
  const router = useRouter();
  const isMobile = useMobile();
  const [mounted, setMounted] = useState(false);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const tabsHost = usePortalHost(
    "header[data-feed-top-bar][data-global-header] [data-feed-tabs-host], header[data-global-header] [data-feed-tabs-host], [data-feed-tabs-host]",
  );
  const hostsHydrated = useFeedSearchHeaderPortalHostsHydrated();
  const [chromeOpacity, setChromeOpacity] = useState(0);
  const [titleOpacity, setTitleOpacity] = useState(0);
  const [compactSearchOpacity, setCompactSearchOpacity] = useState(0);
  const [compactSearchInteractive, setCompactSearchInteractive] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileHeaderRef = useRef<HTMLElement | null>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const mobileSearchScrollYRef = useRef(0);
  const rafRef = useRef(0);

  const mobileTitleVisible = getProfileHeaderTitleVisible(mobileScrollY, mobileCoverHeight);
  const mobileTabsVisible = mobileHeaderTabsOpacity > 0;
  const mobileChromeActive = mobileTitleVisible || mobileSearchOpen || mobileTabsVisible;

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (isMobile || !tabsHost || !hostsHydrated) return;
    setPortalRoot(queryProfileHeaderPortalRoot(tabsHost));
  }, [hostsHydrated, isMobile, tabsHost]);

  useEffect(() => {
    const update = () => {
      rafRef.current = 0;
      const scrollY = window.scrollY;

      if (isMobile) return;

      const heroBottom = heroRef.current?.getBoundingClientRect().bottom ?? 0;
      const heroScrolledPast = heroBottom <= 56;
      const fadeStart = Math.max(0, searchCollapseStart - 56);
      const fadeEnd = fadeStart + 56;
      const chrome =
        scrollY <= fadeStart
          ? 0
          : scrollY >= fadeEnd
            ? 1
            : (scrollY - fadeStart) / (fadeEnd - fadeStart);

      const searchEnd = searchCollapseStart + PROFILE_SEARCH_ROW_HEIGHT;
      let title = heroScrolledPast ? 1 : 0;
      let compactSearch = 0;

      if (searchCollapseStart > 0) {
        if (scrollY < searchCollapseStart) {
          title = heroScrolledPast
            ? Math.min(1, (scrollY - fadeStart) / Math.max(1, fadeEnd - fadeStart))
            : 0;
          compactSearch = 0;
        } else if (scrollY >= searchEnd) {
          title = 0;
          compactSearch = 1;
        } else {
          const progress = (scrollY - searchCollapseStart) / PROFILE_SEARCH_ROW_HEIGHT;
          title = 1 - progress;
          compactSearch = progress;
        }
      }

      setChromeOpacity(chrome);
      setTitleOpacity(title);
      setCompactSearchOpacity(compactSearch);
      setCompactSearchInteractive(scrollY >= searchCollapseStart + PROFILE_SEARCH_ROW_HEIGHT * 0.88);
    };

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [heroRef, isMobile, searchCollapseStart, searchRowRef]);

  useLayoutEffect(() => {
    if (!isMobile) return;
    const root = document.documentElement;
    const el = mobileHeaderRef.current;
    if (!el) return;

    const update = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      root.style.setProperty("--header-height", `${height}px`);
      root.style.setProperty("--fixed-chrome-height", `${height}px`);
      root.style.removeProperty("--subheader-height");
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--header-height");
      root.style.removeProperty("--fixed-chrome-height");
    };
  }, [isMobile, mobileChromeActive, mobileSearchOpen]);

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
      return;
    }
    router.push("/feed");
  };

  const openMobileSearch = () => {
    if (typeof window !== "undefined") {
      mobileSearchScrollYRef.current = window.scrollY;
    }
    setMobileSearchOpen(true);
  };

  const closeMobileSearch = () => {
    onSearchQueryChange("");
    setMobileSearchOpen(false);
  };

  useLayoutEffect(() => {
    if (!isMobile || !mobileSearchOpen) return;

    const savedY = mobileSearchScrollYRef.current;
    window.scrollTo(0, savedY);

    const input = mobileSearchInputRef.current;
    input?.focus({ preventScroll: true });

    const raf = requestAnimationFrame(() => {
      window.scrollTo(0, savedY);
    });

    return () => cancelAnimationFrame(raf);
  }, [isMobile, mobileSearchOpen]);

  const profileUrl = profileShareUrl(username);

  const headerBlurOpacity = Math.max(chromeOpacity, titleOpacity, compactSearchOpacity);
  const showBar = headerBlurOpacity > 0.02;

  useLayoutEffect(() => {
    if (hidden) {
      dispatchProfileStickyCompact(false);
      return () => dispatchProfileStickyCompact(false);
    }
    if (isMobile) {
      dispatchProfileStickyCompact(mobileChromeActive);
      return () => dispatchProfileStickyCompact(false);
    }
    dispatchProfileStickyCompact(showBar);
    return () => dispatchProfileStickyCompact(false);
  }, [hidden, isMobile, mobileChromeActive, mobileSearchOpen, showBar]);

  const mobileIconButtonClass = cn(
    "h-10 w-10 shrink-0 rounded-full active:scale-95 transition-all inline-flex items-center justify-center",
    mobileChromeActive
      ? "hover:bg-secondary/50"
      : "bg-background/30 backdrop-blur-md hover:bg-background/50",
  );

  const mobileHeader = (
    <header
      ref={mobileHeaderRef}
      data-profile-sticky-header
      className="fixed top-0 right-0 left-0 z-50 safe-area-top transition-all duration-300 md:hidden isolate"
    >
      <div
        aria-hidden
        className={cn(
          HUB_OVERLAY_BACKDROP,
          "transition-opacity duration-300",
          mobileChromeActive ? "opacity-100" : "opacity-0",
        )}
        style={PROFILE_STICKY_OVERLAY_MASK}
      />
      <div className="relative z-1">
      <div className="flex h-11 shrink-0 items-center gap-2 px-3">
        <button
          type="button"
          onClick={handleBack}
          className={mobileIconButtonClass}
          aria-label={tInfo("goBack")}
        >
          <ChevronLeft className="h-5 w-5 text-foreground" />
        </button>

        {mobileSearchOpen ? (
          <div className="relative flex h-10 min-w-0 flex-1 items-center rounded-full bg-secondary">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={mobileSearchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder={stickySearchPlaceholder}
              autoCorrect="off"
              autoCapitalize="none"
              enterKeyHint="search"
              aria-label={searchPlaceholder}
              onFocus={() => {
                const savedY = mobileSearchScrollYRef.current;
                requestAnimationFrame(() => window.scrollTo(0, savedY));
              }}
              className="h-10 w-full min-w-0 flex-1 truncate bg-transparent py-0 pl-8 pr-2 text-base leading-none text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
        ) : (
          <div className="relative min-w-0 flex-1">
            <div
              className={cn(
                "min-w-0 overflow-visible transition-all duration-200",
                mobileTitleVisible
                  ? "pointer-events-auto opacity-100 translate-y-0"
                  : "pointer-events-none absolute inset-0 opacity-0 -translate-y-2",
              )}
            >
              <p className="truncate text-sm font-semibold leading-tight text-foreground">{compactTitle}</p>
              <ProfileStickyCompactStats profileId={profileId} salesCount={salesCount} isOwner={isOwner} />
            </div>
          </div>
        )}

        {mobileSearchOpen ? (
          <button
            type="button"
            onClick={closeMobileSearch}
            className={mobileIconButtonClass}
            aria-label={tInfo("goBack")}
          >
            <X className="h-5 w-5 text-foreground" />
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={openMobileSearch}
              className={mobileIconButtonClass}
              aria-label={searchPlaceholder}
            >
              <Search className="h-5 w-5 text-foreground" />
            </button>

            <ShareSheet
              url={profileUrl}
              title={t("shareProfileTitle", { username })}
              image={avatarUrl ?? undefined}
              username={username}
              bio={bio?.trim() || undefined}
              variant="profile"
              side="bottom"
              align="end"
            >
              <button type="button" className={mobileIconButtonClass} aria-label={t("shareProfile")}>
                <Share className="h-5 w-5 text-foreground" />
              </button>
            </ShareSheet>

            <ProfileActionsMenu
              profileId={profileId}
              username={username}
              avatarUrl={avatarUrl}
              currentUserId={currentUserId ?? undefined}
              triggerClassName={mobileIconButtonClass}
              iconClassName="h-5 w-5"
              showShareInMenu={false}
            />
          </>
        )}
      </div>

      <div
        className="pb-1 transition-all duration-200"
        style={{
          opacity: mobileHeaderTabsOpacity,
          height: mobileHeaderTabsOpacity > 0 ? 44 : 0,
          overflow: "hidden",
        }}
      >
        <ProfileTabBar
          listingsCount={shopListingsCount}
          variant="compact"
          sticky={false}
        />
      </div>
      </div>
    </header>
  );

  const barContent = (
    <div
      data-profile-sticky-header
      className={cn(
        "w-full transition-[max-height,opacity] duration-150",
        showBar ? "max-h-[4.5rem] opacity-100" : "pointer-events-none max-h-0 overflow-hidden opacity-0",
      )}
    >
      <div className="relative mx-auto flex min-h-[4.5rem] w-full max-w-(--route-max-width,64rem) items-center gap-1 px-4 py-1 leading-normal">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-full"
          onClick={handleBack}
          aria-label={tInfo("goBack")}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        <div className="relative min-h-12 min-w-0 flex-1 self-stretch overflow-visible">
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-0 px-2 transition-opacity duration-150"
            style={{ opacity: titleOpacity, pointerEvents: titleOpacity > 0.5 ? "auto" : "none" }}
          >
            <span className="max-w-full truncate text-sm font-semibold leading-tight">{compactTitle}</span>
            <ProfileStickyCompactStats profileId={profileId} salesCount={salesCount} isOwner={isOwner} />
          </div>

          <div
            className="absolute inset-0 flex items-center transition-opacity duration-150"
            style={{
              opacity: compactSearchOpacity,
              pointerEvents: compactSearchInteractive ? "auto" : "none",
            }}
          >
            <ProfileSearchField
              value={searchQuery}
              onChange={onSearchQueryChange}
              placeholder={stickySearchPlaceholder}
              ariaLabel={searchPlaceholder}
              variant="compact"
              className="w-full"
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <ShareSheet
            url={profileUrl}
            title={t("shareProfileTitle", { username })}
            image={avatarUrl ?? undefined}
            username={username}
            bio={bio?.trim() || undefined}
            variant="profile"
            side="bottom"
            align="end"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full"
              aria-label={t("shareProfile")}
            >
              <Share className="h-5 w-5" />
            </Button>
          </ShareSheet>
          <ProfileActionsMenu
            profileId={profileId}
            username={username}
            avatarUrl={avatarUrl}
            currentUserId={currentUserId ?? undefined}
            triggerClassName="h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-muted/80"
            iconClassName="h-5 w-5"
            showShareInMenu={false}
          />
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    if (hidden) return null;
    return mobileHeader;
  }

  if (!mounted || !portalRoot) return null;
  return createPortal(barContent, portalRoot);
}
